import type { TypographyVariantsOptions } from "@mui/material/styles";
import type { CSSProperties } from "react";
import { palette } from "./palette";

declare module "@mui/material/styles" {
  interface TypographyVariants {
    readonly h1: CSSProperties;
    readonly h2: CSSProperties;
    readonly h3: CSSProperties;
    readonly h4: CSSProperties;
    readonly subtitle1: CSSProperties;
    readonly subtitle1SemiBold: CSSProperties;
    readonly subtitle1Bold: CSSProperties;
    readonly subtitle2: CSSProperties;
    readonly subtitle2SemiBold: CSSProperties;
    readonly body1: CSSProperties;
    readonly body1SemiBold: CSSProperties;
    readonly body1Bold: CSSProperties;
    readonly body2: CSSProperties;
    readonly body2Underlined: CSSProperties;
    readonly body2SemiBold: CSSProperties;
    readonly body2Bold: CSSProperties;
    readonly button: CSSProperties;
    readonly caption: CSSProperties;
    readonly captionBold: CSSProperties;
    readonly overline: CSSProperties;
    readonly itemNumber: CSSProperties;
  }
  interface TypographyVariantsOptions {
    readonly h1: CSSProperties;
    readonly h2: CSSProperties;
    readonly h3: CSSProperties;
    readonly h4: CSSProperties;
    readonly subtitle1: CSSProperties;
    readonly subtitle1SemiBold: CSSProperties;
    readonly subtitle1Bold: CSSProperties;
    readonly subtitle2: CSSProperties;
    readonly subtitle2SemiBold: CSSProperties;
    readonly body1: CSSProperties;
    readonly body1SemiBold: CSSProperties;
    readonly body1Bold: CSSProperties;
    readonly body2: CSSProperties;
    readonly body2Underlined: CSSProperties;
    readonly body2SemiBold: CSSProperties;
    readonly body2Bold: CSSProperties;
    readonly button: CSSProperties;
    readonly caption: CSSProperties;
    readonly captionBold: CSSProperties;
    readonly overline: CSSProperties;
    readonly itemNumber: CSSProperties;
  }
}

declare module "@mui/material" {
  interface TypographyPropsVariantOverrides {
    readonly h1: true;
    readonly h2: true;
    readonly h3: true;
    readonly h4: true;
    readonly subtitle1: true;
    readonly subtitle1SemiBold: true;
    readonly subtitle1Bold: true;
    readonly subtitle2: true;
    readonly subtitle2SemiBold: true;
    readonly body1: true;
    readonly body1SemiBold: true;
    readonly body1Bold: true;
    readonly body2: true;
    readonly body2Underlined: true;
    readonly body2SemiBold: true;
    readonly body2Bold: true;
    readonly button: true;
    readonly caption: true;
    readonly captionBold: true;
    readonly overline: true;
    readonly itemNumber: true;
  }
}

export const typography = {
  h1: {
    fontFamily: "Bison",
    fontWeight: 700,
    fontSize: "22px",
    textTransform: "uppercase",
    color: palette.primary.dark,
  },
  h2: {
    fontFamily: "Source Sans Pro",
    fontWeight: 700,
    fontSize: "18px",
    color: palette.primary.dark,
  },
  h3: {
    fontFamily: "Bison",
    fontWeight: 700,
    fontSize: "17px",
    textTransform: "uppercase",
  },
  h4: {
    fontFamily: "Source Sans Pro",
    fontWeight: 600,
    fontSize: "17px",
  },
  subtitle1: {
    fontFamily: "Source Sans Pro",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "20px",
  },
  subtitle1SemiBold: {
    fontFamily: "Source Sans Pro",
    fontWeight: 600,
    fontSize: "16px",
  },
  subtitle1Bold: {
    fontFamily: "Source Sans Pro",
    fontWeight: 700,
    fontSize: "16px",
  },
  subtitle2: {
    fontFamily: "Source Sans Pro",
    fontWeight: 400,
    fontSize: "15px",
  },
  subtitle2SemiBold: {
    fontFamily: "Source Sans Pro",
    fontWeight: 600,
    fontSize: "15px",
  },
  body1: {
    fontFamily: "Source Sans Pro",
    fontWeight: 400,
    fontSize: "14px",
  },
  body1SemiBold: {
    fontFamily: "Source Sans Pro",
    fontWeight: 600,
    fontSize: "14px",
    letterSpacing: "0.5px",
  },
  body1Bold: {
    fontFamily: "Source Sans Pro",
    fontWeight: 700,
    fontSize: "14px",
  },
  body2: {
    fontFamily: "Source Sans Pro",
    fontWeight: 400,
    fontSize: "12px",
  },
  body2Underlined: {
    fontFamily: "Source Sans Pro",
    fontWeight: 600,
    fontSize: "12px",
    textDecorationLine: "underline",
  },
  body2SemiBold: {
    fontFamily: "Source Sans Pro",
    fontWeight: 600,
    fontSize: "12px",
  },
  body2Bold: {
    fontFamily: "Source Sans Pro",
    fontWeight: 700,
    fontSize: "12px",
  },
  button: {
    fontFamily: "Bison",
    fontWeight: 700,
    fontSize: "16px",
    letterSpacing: "1.25px",
    textTransform: "uppercase",
  },
  caption: {
    fontFamily: "Source Sans Pro",
    fontWeight: 400,
    fontSize: "13px",
    lineHeight: "16px",
    letterSpacing: "0.4px",
  },
  captionBold: {
    fontFamily: "Source Sans Pro",
    fontSize: "13px",
    lineHeight: "16px",
    letterSpacing: "0.4px",
    fontWeight: 600,
  },
  overline: {
    fontFamily: "Source Sans Pro",
    fontSize: "11px",
    letterSpacing: "1.5px",
    textTransform: "uppercase",
  },
  h5: {
    fontFamily: "Source Sans Pro",
    fontWeight: 600,
    fontSize: "1.5rem",
  },
  h6: {
    fontFamily: "Source Sans Pro",
    fontWeight: 500,
    fontSize: "20px",
  },
  itemNumber: {
    fontFamily: "Aldrich",
    fontWeight: "600",
    fontSize: "12px",
    color: palette.primary.main,
  },
} as const satisfies TypographyVariantsOptions;
